// src/auth/screens/TokenPasswordScreen.jsx
//
// One component, two routes: /password-reset?token=… and /set-password?token=…
// They differ only in the endpoint, the 400 error_code and the title copy —
// token read, password fields, requirement checklist and the per-reason invalid
// panel are identical.
//
// ⭐ v221 — `set` only (variant.consents): the REQUIRED terms box + the OPTIONAL marketing box
// (useSetPasswordConsents → legal/TermsCheckbox). The submit stays disabled until the terms are read AND ticked;
// a 409 TERMS_VERSION_CHANGED takes the new texts from its body, unticks, and shows the amber AuthNotice — the
// link is still valid. /set-password/info's 400 shows the invalid panel before anyone types. `reset` is unchanged.
//
// ⚠️ THE TOKEN IS A CREDENTIAL. Never log it, never interpolate it into a
// message, and leave the URL with { replace: true } once it has been spent.
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import AuthCard, { AuthNotice, authLabelStyle } from './AuthCard.jsx';
import { confirmPasswordReset, setPassword as setPasswordApi } from '../passwordResetApi.js';
import { authFallbackCopy } from '../authErrorCopy.js';
import { checkPassword, isPasswordValid, PASSWORD_RULES } from '../passwordPolicy.js';
import useSetPasswordConsents from '../useSetPasswordConsents.js';
import TermsCheckbox from '../../legal/TermsCheckbox.jsx';
import { isTermsConflict } from '../../legal/termsModel.js';
import { COLORS, SEMANTIC } from '../../constants/theme.js';

const VARIANTS = {
  reset: {
    submit: confirmPasswordReset,
    invalidCode: 'PASSWORD_RESET_TOKEN_INVALID',
    titleKey: 'auth:resetPassword.title',
    introKey: 'auth:resetPassword.intro',
    // ⛔ NOT shared with `set`: that user never HAD a password, so "updated"
    // is factually wrong for them. LoginScreen maps each to its own copy.
    notice: 'password-reset',
    consents: false,
  },
  set: {
    submit: setPasswordApi,
    invalidCode: 'SET_PASSWORD_TOKEN_INVALID',
    titleKey: 'auth:setPassword.title',
    introKey: 'auth:setPassword.intro',
    notice: 'password-set',
    consents: true,
  },
};

// The backend caps `token` at 256 chars (SetPasswordRequest /
// PasswordResetConfirmRequest), so a longer one is rejected by the body
// validator as a 422 — which this screen would word as "password non valida",
// i.e. a bad LINK reported as a bad PASSWORD. Catch it here instead, on the
// same no-network principle as MISSING.
const TOKEN_MAX = 256;

// The four reasons the FE can end up rendering. MISSING is FE-only (no token in
// the query string at all); the other three come from the 400 body's `reason`.
const KNOWN_REASONS = new Set(['MISSING', 'UNKNOWN', 'EXPIRED', 'ALREADY_USED']);

export default function TokenPasswordScreen({ variant }) {
  const config = VARIANTS[variant];
  const { t } = useTranslation(['auth', 'errors', 'legal']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Non-null → the token is dead and the form is replaced by the invalid panel.
  const [deadReason, setDeadReason] = useState(
    token ? (token.length > TOKEN_MAX ? 'UNKNOWN' : null) : 'MISSING'
  );
  const consents = useSetPasswordConsents({
    token,
    enabled: config.consents && !deadReason,
    onDeadLink: (reason) => setDeadReason(KNOWN_REASONS.has(reason) ? reason : 'UNKNOWN'),
  });

  const rules = checkPassword(password);
  const matches = password.length > 0 && password === confirm;
  const consentsOk = !config.consents || consents.ready;
  const canSubmit = isPasswordValid(password) && matches && consentsOk && !loading;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!isPasswordValid(password)) { setError(t('auth:passwordForm.invalid')); return; }
    if (!matches) { setError(t('auth:passwordForm.mismatch')); return; }
    if (!consentsOk) return; // the button is disabled; this guards a programmatic submit

    setLoading(true);
    try {
      await config.submit({ token, password, ...(config.consents ? consents.submitFields() : {}) });
      // 204. Leave the token behind in history and hand off to /login with a
      // one-shot notice.
      navigate('/login', { replace: true, state: { notice: config.notice } });
    } catch (err) {
      if (err.code === 'NETWORK') {
        setError(t('errors:auth.network'));
      } else if (err.status === 400 && err.code === config.invalidCode) {
        // ⭐ Disclosing WHICH reason is safe (256 bits of CSPRNG) and is what
        // lets us offer a fresh link instead of a dead end.
        const reason = KNOWN_REASONS.has(err.reason) ? err.reason : 'UNKNOWN';
        setDeadReason(reason);
      } else if (config.consents && isTermsConflict(err)) {
        // ⭐ v221: a new version was published while the page was open. The link is NOT spent: new texts from the
        // 409 body, box unticked, amber notice; the person ticks again and resubmits.
        consents.terms.applyConflict(err);
      } else if (err.status === 422) {
        // On an unauthenticated route the body validator is the only gate; the
        // client-side check above should already have caught this.
        setError(t('auth:passwordForm.invalid'));
      } else {
        // ⛔ NEVER `err.message` here. buildApiError always sets a non-empty
        // message — the server's English text, or errors:context.generic — so
        // an `err.message || t(…)` fallback is unreachable and an Italian user
        // reads the backend verbatim on a 500/503. Same defect billing fixed
        // in refusalCopy.js; branch on the code, fall back to OUR copy, which
        // prints the code (or the 5xx reference) — authErrorCopy.js.
        setError(authFallbackCopy(t, err, 'errors:auth.passwordSaveFailed'));
      }
    } finally {
      setLoading(false);
    }
  }

  if (deadReason) {
    return (
      <AuthCard title={t('auth:tokenInvalid.title')}>
        <AuthNotice tone="error">{t(`auth:tokenInvalid.${deadReason}`)}</AuthNotice>
        <Link to="/forgot-password" className="auth-submit"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
          {t('auth:tokenInvalid.requestNew')}
        </Link>
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link to="/login" className="auth-link">{t('auth:tokenInvalid.goToLogin')}</Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t(config.titleKey)} intro={t(config.introKey)}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '18px' }}>
          <label htmlFor="new-password" style={authLabelStyle}>{t('auth:passwordForm.newLabel')}</label>
          <input
            id="new-password"
            type="password"
            className="auth-input"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            autoFocus
          />
        </div>

        <div style={{ marginBottom: '18px' }}>
          <label htmlFor="confirm-password" style={authLabelStyle}>{t('auth:passwordForm.confirmLabel')}</label>
          <input
            id="confirm-password"
            type="password"
            className="auth-input"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>

        {/* ⚠️ UX only — the backend enforces 8–128 characters and nothing else. */}
        <div style={{ marginBottom: '18px' }}>
          <div style={{ ...authLabelStyle, marginBottom: '10px' }}>
            {t('auth:passwordForm.requirementsTitle')}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px' }}>
            {PASSWORD_RULES.map(rule => {
              const ok = rules[rule];
              const key = `req${rule.charAt(0).toUpperCase()}${rule.slice(1)}`;
              return (
                <li key={rule}
                    data-testid={`rule-${rule}`}
                    data-ok={ok ? 'true' : 'false'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      fontSize: '12px',
                      color: ok ? SEMANTIC.verde.fg : COLORS.textSecondary,
                    }}>
                  {ok ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
                  {t(`auth:passwordForm.${key}`)}
                </li>
              );
            })}
          </ul>
        </div>

        {config.consents && consents.terms.changed && (
          <AuthNotice tone="warning">{t('legal:terms.changed')}</AuthNotice>
        )}
        {config.consents && (
          <div style={{ marginBottom: '18px' }}>
            <TermsCheckbox terms={consents.terms} marketing={consents.marketing}
                           disabled={loading} showChanged={false} />
          </div>
        )}
        {confirm.length > 0 && !matches && (
          <AuthNotice tone="error">{t('auth:passwordForm.mismatch')}</AuthNotice>
        )}
        {error && <AuthNotice tone="error">{error}</AuthNotice>}

        <button type="submit" className="auth-submit" disabled={!canSubmit}>
          {loading ? t('auth:passwordForm.submitLoading') : t('auth:passwordForm.submit')}
        </button>
      </form>
    </AuthCard>
  );
}
